import React, { useEffect, useState } from 'react';
import { GameMoveModel } from '../types/GamesModel';
import { useAppDispatch, useAppSelector } from '../store';
import { setParams } from '../reducers/paramsReducer';
import GamesAPI from '../api/GamesAPI';
import { Badge, Dropdown } from 'react-bootstrap';
import { BarLoader } from 'react-spinners';
import { getIcon } from '../utils/ChessTypes';
import { setGameIdForModal } from '../reducers/uiReducer';

// A dropdown of moves related to the current move, scoped to the active filters (group included):
//  - mode 'siblings' (default): other moves from the same parent position.
//  - mode 'continuations': moves played from the position after this one.
// Without a `label` it's the compact caret in the filter breadcrumb: lazy, gated on the global
// HasAlternatives hint. With a `label` it's the move panel's dropdown: HasAlternatives is global and can
// over-report in a group, so it fetches the scoped list up front and renders only when it holds a move
// other than `currentPgn`.
export default function GamesMoveAlternatives(props: {
  move: GameMoveModel;
  mode?: 'siblings' | 'continuations';
  label?: string;
  // The move to highlight and exclude when deciding "are there others": this move for siblings, the
  // game's own continuation for continuations.
  currentPgn?: string;
}): React.ReactNode {
  const { move, mode = 'siblings', label, currentPgn } = props;
  const eager = label != null;
  const highlightPgn = currentPgn ?? move.Pgn;
  const [alternatives, setAlternatives] = useState<GameMoveModel[]>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const { username, color, platform, timeControl, from, to, group } = useAppSelector(
    (state) => state.paramsReducer.params,
  );
  const dispatch = useAppDispatch();

  const fetchAlternatives = () => {
    setLoading(true);
    setError(undefined);
    return GamesAPI.getAlternatives(
      move.Pgn,
      mode === 'continuations',
      username ?? null,
      color ?? null,
      platform ?? null,
      timeControl ?? null,
      from ?? null,
      to ?? null,
      group ?? null,
    );
  };

  // Panel dropdowns fetch up front. Re-runs when the move or a filter changes; the flag drops a late
  // response from a superseded move.
  useEffect(() => {
    if (!eager) {
      return;
    }
    let cancelled = false;
    setAlternatives(undefined);
    fetchAlternatives()
      .then((data) => {
        if (!cancelled) {
          setAlternatives(data);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eager, move.Pgn, mode, username, color, platform, timeControl, from, to, group]);

  // The compact caret fetches on first open.
  const loadOnOpen = () => {
    if (alternatives != null || loading) {
      return;
    }
    fetchAlternatives()
      .then((data) => {
        setAlternatives(data);
        setLoading(false);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  };

  // Filter the list to this line and close the overlay, so the user lands on the games that played it.
  const jumpTo = (altPgn: string) => {
    dispatch(setParams({ opening: null, timeControl: null, pgn: altPgn, page: 0 }));
    dispatch(setGameIdForModal(null));
  };

  // Compact caret: hidden when the move has no siblings (the global hint is enough).
  if (!eager && !move.HasAlternatives) {
    return null;
  }

  // Panel dropdown: hidden until the scoped fetch shows a move other than the one in this game.
  const hasRealAlternatives = (alternatives ?? []).some((a) => a.Pgn !== highlightPgn);
  if (eager && !hasRealAlternatives) {
    return null;
  }

  const empty = alternatives != null && alternatives.length === 0;

  // The menu uses strategy 'fixed', so percentages resolve against the viewport; copy the toggle's width
  // in px.
  const matchToggleWidth = {
    name: 'cmMatchToggleWidth',
    enabled: true,
    phase: 'beforeWrite' as const,
    requires: ['computeStyles'],
    fn: ({ state }: any) => {
      state.styles.popper.minWidth = `${state.rects.reference.width}px`;
    },
    effect: ({ state }: any) => {
      state.elements.popper.style.minWidth = `${(state.elements.reference as HTMLElement).offsetWidth}px`;
    },
  };

  return (
    <Dropdown
      className={eager ? 'cm-alt-dd' : undefined}
      onToggle={(isOpen) => !eager && isOpen && loadOnOpen()}
    >
      {eager ? (
        <Dropdown.Toggle as="button" type="button" className="cm-alt-dd-toggle">
          {label}
        </Dropdown.Toggle>
      ) : (
        <Dropdown.Toggle as="a" className="pointer cm-alt-toggle"></Dropdown.Toggle>
      )}
      <Dropdown.Menu
        align="start"
        className={eager ? 'cm-alt-dd-menu' : undefined}
        renderOnMount
        popperConfig={{ strategy: 'fixed', modifiers: eager ? [matchToggleWidth] : [] }}
      >
        {loading
          ? [0, 1].map((i) => (
              <Dropdown.Item as="div" key={`skeleton-${i}`} disabled className="cm-alt-skeleton">
                <BarLoader color="#888" width={160} height={6} />
              </Dropdown.Item>
            ))
          : null}
        {error ? (
          <Dropdown.Item as="div" disabled className="cm-alt-empty text-danger">
            {error}
          </Dropdown.Item>
        ) : null}
        {!loading && !error && empty ? (
          <Dropdown.Item as="div" disabled className="cm-alt-empty">
            No other moves recorded.
          </Dropdown.Item>
        ) : null}
        {!loading &&
          alternatives?.map((alt, i) => {
            const Icon = getIcon(alt.MoveType ?? undefined);
            const isCurrent = alt.Pgn === highlightPgn;
            return (
              <Dropdown.Item
                key={i}
                // The move already on the board, shown disabled.
                disabled={isCurrent}
                className={isCurrent ? 'cm-alt-current' : ''}
                title={alt.Comment ?? ''}
                onClick={() => jumpTo(alt.Pgn)}
              >
                <span className="d-flex align-items-center">
                  <Icon className="me-1" />
                  <span className="me-1" title={alt.Pgn}>
                    {alt.NewPosition}
                  </span>
                  <Badge className="bg me-2">{alt.Count}</Badge>
                  <span>{alt.Comment}</span>
                </span>
              </Dropdown.Item>
            );
          })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
